/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import {
    DatePicker, NumberInput, Title,
    TextInput,
} from 'ui-library';
import moment from 'moment';

import Input from 'components/AtomicStructure/atoms/Input';
import SelectElement from 'components/AtomicStructure/atoms/SelectElement';
import PageSection from 'components/PageSection/PageSection';
import Preloader from 'components/Preloader';
import { withMainLayout } from 'hocs';
import {
    compareDates, formatDateWithoutLocalConversion,
} from 'utils/datetime';
import { validateIban } from 'utils/validation';
import { isQRIban } from 'utils';
import { DEFAULT_APP_CURRENCY } from 'constants/constants';
import WithDrawalPlanSummary from './WithDrawalPlanSummary';
import './WithdrawalPlan.css';

const WithdrawalPlan = ({ datasource }) => {
    const { t } = useTranslation();
    const {
        data,
        setData,
        withdrawalPlanData,
        currentWithdrawalData,
        planCalculationLoading,
        errorData,
        isRegularTransferPlan,
        optionWithdrawalPeriodicity,
        isLoading,
        isOptionsLoading,
        productExternalId,
        productDesc,
        isAzp,
        isAzpI,
        isAzpD,
        isPk,
        isZiv,
        isZifd,
        trigger,
        setErrorData,
        displayWithdrawalPlanCalculations,
        disableDateHandler,
        control,
    } = datasource();

    const getSelectedTitle = () => {
        if (isAzp) {
            return t('3b.withdrawalPlan.defineWithdrawalPlan');
        } if (isZifd || isZiv) {
            return t('3b.withdrawalPlan.setupWithdrawalTitle');
        } if (isPk && isRegularTransferPlan) {
            return t('3b.withdrawalPlan.setupTransferTitle');
        }

        return null;
    };

    const handleFrequencyOnChange = useCallback((val) => {
        if (val !== currentWithdrawalData?.frequency) {
            setData({
                ...data, frequency: val, startDate: moment(currentWithdrawalData?.startOfWithdrawal)?.format('MM/YYYY'), withdrawalsStartDate: moment(currentWithdrawalData?.startOfWithdrawal)?.format('MM/YYYY'),
            });
        } else {
            setData({
                ...data, frequency: val,
            });
        }
    }, [currentWithdrawalData?.startOfWithdrawal, currentWithdrawalData?.frequency, data]);

    return (
        <PageSection className="newStyle withdrawalPlan">
            <div>
                <Preloader isLoading={isLoading || isOptionsLoading} >
                    <div>
                        {(isAzp || isZifd) && (
                            <Title type={1}>
                                {productDesc}
                            </Title>
                        )}
                        {isZifd && (
                            <Title type={2}>
                                {t('3b.withdrawalPlan.subtitle')}
                            </Title>
                        )}
                        <div className="withdrawalPlanGrid">
                            <div className="form">
                                <fieldset className="setup-section">

                                    {getSelectedTitle() && (
                                        <Title type={3} tagName="legend">
                                            {getSelectedTitle()}
                                        </Title>
                                    )}

                                    {isAzp && (
                                        <div className="form">
                                            <Controller
                                                name="amount"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <NumberInput
                                                        {...field}
                                                        placeholder={t('3b.withdrawalPlan.investmentAmountPlaceholder')}
                                                        thousandSeparator="’"
                                                        decimalSeparator="."
                                                        label={t('3b.withdrawalPlan.investmentAmount')}
                                                        value={data.amount}
                                                        onChange={(value: number) => {
                                                            onChange(value);
                                                            trigger('amount');
                                                        }}
                                                        error={errorData?.amount?.message}
                                                        required
                                                        allowNegativeValue={false}
                                                        prefix={`${DEFAULT_APP_CURRENCY} `}
                                                    />
                                                )}
                                            />

                                            <div className="withdrawals-start">
                                                <Controller
                                                    name="withdrawalsStartDate"
                                                    control={control}
                                                    render={({ field: { onChange, ...field } }) => (
                                                        <DatePicker
                                                            {...field}
                                                            /* @ts-ignore */
                                                            label={t('3b.withdrawalPlan.withdrawalsStart')}
                                                            value={data?.withdrawalsStartDate && formatDateWithoutLocalConversion(data.withdrawalsStartDate, 'DD.MM.YYYY')}
                                                            placeholder={t('3b.withdrawalPlan.withdrawalsStartPlaceholder')}
                                                            disabledDate={disableDateHandler}
                                                            error={errorData?.withdrawalsStartDate?.message}
                                                            format="DD.MM.YYYY"
                                                            required
                                                            onChange={(value) => {
                                                                const date = moment(value).format('YYYY-MM-DD');

                                                                onChange(date);
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <span>
                                                    {t('3b.withdrawalPlan.withdrawalsStartNote')}
                                                        &nbsp;
                                                    {isAzpI && t('validation.dateNotWithinTwelveMonths')}
                                                    {isAzpD && t('validation.dateWithinTwelveMonths')}
                                                </span>
                                            </div>
                                            <Controller
                                                name="duration"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <NumberInput
                                                        {...field}
                                                        data-cy="duration"
                                                        placeholder={t('3b.withdrawalPlan.durationPlaceholder')}
                                                        label={t('3b.withdrawalPlan.duration')}
                                                        value={data?.duration}
                                                        error={errorData?.duration?.message ? t(errorData?.duration?.message) : ''}
                                                        required
                                                        onChange={(value: number) => {
                                                            onChange(value);
                                                            trigger('duration');
                                                        }}
                                                        allowNegativeValue={false}
                                                        precision={0}
                                                    />
                                                )}
                                            />

                                            <Controller
                                                name="frequency"
                                                control={control}
                                                render={({ field }) => (
                                                    <SelectElement
                                                        {...field}
                                                        data-cy="periodicity"
                                                        /* @ts-ignore */
                                                        placeholder={t('3b.kyc.pleaseSelect')}
                                                        label={t('3b.withdrawalPlan.periodicity')}
                                                        value={data?.frequency}
                                                        options={optionWithdrawalPeriodicity}
                                                        error={errorData?.frequency?.message}
                                                        required
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}

                                    {isZifd && (
                                        <div className="form">
                                            <Controller
                                                name="amount"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <NumberInput
                                                        {...field}
                                                        placeholder={t('3b.withdrawalPlan.withdrawalAmountPlaceholder')}
                                                        thousandSeparator="’"
                                                        decimalSeparator="."
                                                        label={t('3b.withdrawalPlan.amount')}
                                                        value={data.amount}
                                                        onChange={(value: number) => {
                                                            onChange(value);
                                                            trigger('amount');
                                                        }}
                                                        error={errorData?.amount?.message}
                                                        required
                                                        allowNegativeValue={false}
                                                        prefix={`${DEFAULT_APP_CURRENCY} `}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="frequency"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <SelectElement
                                                        {...field}
                                                        data-cy="withdrawalFrequency"
                                                        /* @ts-ignore */
                                                        placeholder={t('3b.kyc.pleaseSelect')}
                                                        label={t('3b.withdrawalPlan.frequency')}
                                                        value={data?.frequency}
                                                        options={optionWithdrawalPeriodicity}
                                                        error={errorData?.frequency?.message}
                                                        required
                                                        onChange={currentWithdrawalData?.frequency ? handleFrequencyOnChange : onChange}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="startDate"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <DatePicker
                                                        {...field}
                                                        /* @ts-ignore */
                                                        label={t('3b.withdrawalPlan.withdrawalsStart')}
                                                        value={data?.startDate}
                                                        disabledDate={(current) => current && (compareDates(new Date(), current))}
                                                        onChange={(value) => {
                                                            const date = moment(value).format('MM/YYYY');

                                                            onChange(date);
                                                        }}
                                                        error={errorData?.startDate?.message}
                                                        format="MM/YYYY"
                                                        picker="month"
                                                        required={!(currentWithdrawalData?.frequency && data?.frequency && currentWithdrawalData?.frequency !== +data?.frequency)}
                                                        disabled={(currentWithdrawalData?.frequency && data?.frequency && currentWithdrawalData?.frequency !== +data?.frequency)}
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}
                                </fieldset>

                                <fieldset className="bank-section">
                                    <Title type={3} tagName="legend">
                                        {t('3b.withdrawalPlan.bankTitle')}
                                    </Title>
                                    <div className="form">
                                        <Controller
                                            name="iban"
                                            control={control}
                                            render={({ field: { onChange, ...field } }) => (
                                                <Input
                                                    {...field}
                                                    data-cy="iban"
                                                    label={t('3b.withdrawalPlan.bankIban')}
                                                    value={data?.iban}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        onChange(e.target.value);
                                                        setErrorData('iban', { message: '' });
                                                    }}
                                                    error={errorData?.iban?.message}
                                                    onBlur={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setErrorData('iban', {
                                                            message: await validateIban(e?.target?.value) as string,
                                                        });
                                                    }}
                                                    required
                                                />
                                            )}
                                        />

                                        <Controller
                                            name="esr"
                                            control={control}
                                            render={({ field: { onChange, ...field } }) => (
                                                <TextInput
                                                    {...field}
                                                    data-cy="esr"
                                                    label={t('3b.withdrawalPlan.esr')}
                                                    value={data?.esr}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        onChange(e.target.value);
                                                        setErrorData('esr', { message: '' });
                                                    }}
                                                    error={errorData?.esr?.message}
                                                    required={isQRIban(data?.iban)}
                                                    labelInfo={t('3b.withdrawalPlan.esr.info')}
                                                />
                                            )}
                                        />

                                        <Controller
                                            name="firstName"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    data-cy="firstName"
                                                    label={t('clientDetails.firstName')}
                                                    value={data?.firstName}
                                                    error={errorData?.firstName?.message}
                                                    required
                                                />
                                            )}
                                        />

                                        <Controller
                                            name="lastName"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    data-cy="lastName"
                                                    label={t('clientDetails.lastName')}
                                                    value={data?.lastName}
                                                    error={errorData?.lastName?.message}
                                                    required
                                                />
                                            )}
                                        />

                                        <Controller
                                            name="bankName"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    data-cy="bankName"
                                                    label={t('3b.withdrawalPlan.bankName')}
                                                    value={data?.bankName}
                                                    error={errorData?.bankName?.message}
                                                    required
                                                />
                                            )}
                                        />

                                        <div className="bankZip">
                                            <Controller
                                                name="zipCode"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <Input
                                                        {...field}
                                                        data-cy="bankZip"
                                                        label={t('3b.withdrawalPlan.bankZipCode')}
                                                        value={data?.zipCode}
                                                        error={errorData?.zipCode?.message}
                                                        onChange={(value: number) => {
                                                            onChange(value);
                                                            trigger('zipCode');
                                                        }}
                                                        required
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="city"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        data-cy="bankCity"
                                                        label={t('3b.withdrawalPlan.bankCity')}
                                                        value={data?.city}
                                                        error={errorData?.city?.message}
                                                        required
                                                    />
                                                )}
                                            />

                                        </div>
                                    </div>
                                </fieldset>
                            </div>

                            {isAzp && (
                                <WithDrawalPlanSummary
                                    display={displayWithdrawalPlanCalculations}
                                    planCalculationLoading={planCalculationLoading}
                                    isAzpD={isAzpD}
                                    productExternalId={productExternalId}
                                    withdrawalPlanData={withdrawalPlanData}
                                />
                            )}
                        </div>
                    </div>
                </Preloader>
            </div>
        </PageSection>
    );
};

export default withMainLayout(WithdrawalPlan);
